import { Component } from "react";
import type { ReactNode, ErrorInfo } from "react";

interface ErrorBoundaryProps {
	children: ReactNode;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
}

/**
 * Top-level error boundary that catches render errors in the React tree.
 *
 * Without this, an uncaught render error causes React 18 to unmount the
 * entire tree — leaving the user with a blank page and no diagnostics.
 * This boundary logs the error and shows a recovery screen with a
 * "Reload" button.
 */
export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false, error: null };
	}

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, info: ErrorInfo): void {
		console.error("[ErrorBoundary] Uncaught render error:", error);
		console.error("[ErrorBoundary] Component stack:", info.componentStack);
	}

	handleReload = (): void => {
		window.location.reload();
	};

	render(): ReactNode {
		if (this.state.hasError) {
			return (
				<div style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					height: "100vh",
					fontFamily: "system-ui, sans-serif",
					padding: "2rem",
					textAlign: "center",
					background: "#f5f7fa",
					color: "#1a1a2e",
				}}>
					<h1 style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>
						Something went wrong
					</h1>
					<p style={{ color: "#666", marginBottom: "1rem", maxWidth: "400px" }}>
						An unexpected error occurred. Please reload the page to try again.
					</p>
					{this.state.error && (
						<pre style={{
							fontSize: "0.75rem",
							color: "#c0392b",
							background: "#fef2f2",
							padding: "0.75rem 1rem",
							borderRadius: "6px",
							maxWidth: "500px",
							overflow: "auto",
							marginBottom: "1rem",
							whiteSpace: "pre-wrap",
							wordBreak: "break-word",
						}}>
							{this.state.error.message}
						</pre>
					)}
					<button
						type="button"
						onClick={this.handleReload}
						style={{
							padding: "0.5rem 1.5rem",
							fontSize: "0.875rem",
							fontWeight: 600,
							background: "#1B6BC4",
							color: "#fff",
							border: "none",
							borderRadius: "6px",
							cursor: "pointer",
						}}
					>
						Reload Page
					</button>
				</div>
			);
		}

		return this.props.children;
	}
}
